import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";

import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";
import SamePagePulse from "@/components/SamePagePulse";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import CareersPage from "@/pages/CareersPage";
import ContactPage from "@/pages/ContactPage";
import CommercialTransactionsPage from "@/pages/CommercialTransactionsPage";
import CorporateProfilePage from "@/pages/CorporateProfilePage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import WhatsNewPage from "@/pages/WhatsNewPage";
import RoadmapPage from "@/pages/RoadmapPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import OurTeamPage from "@/pages/OurTeamPage";
import LeadershipPage from "@/pages/LeadershipPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import FoundersMessagePage from "@/pages/FoundersMessagePage";
import SitemapPage from "@/pages/SitemapPage";
import NotFoundPage from "@/pages/NotFoundPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import TechnologyPage from "@/pages/TechnologyPage";
import TrustSecurityPage from "@/pages/TrustSecurityPage";
import SignboardsPage from "@/pages/SignboardsPage";
import MapDetailPage from "@/pages/MapDetailPage";
import SignboardDetailPage from "@/pages/SignboardDetailPage";
import AdminSyncStatusPage from "@/pages/AdminSyncStatusPage";
import { DEFAULT_LANG } from "@/lib/constants";

import "@/App.css";

const SUPPORTED = new Set(["ja", "en"]);

function LangLayout() {
  const { lang } = useParams();
  const { pathname, search, hash } = useLocation();
  if (!SUPPORTED.has(lang)) {
    // The first segment of the URL is not a supported language code.
    // That means the visitor landed on something like `/signboards` or
    // `/whats-new` without the `/ja` or `/en` prefix (a common case when
    // an external link is shared without the language segment). Forward
    // them to the JA-prefixed equivalent so the page resolves. If the
    // resulting path is genuinely unknown, the inner catch-all surfaces
    // the friendly 404 inside the JA layout.
    return (
      <Navigate
        to={`/${DEFAULT_LANG}${pathname}${search}${hash}`}
        replace
      />
    );
  }
  return (
    <Layout lang={lang}>
      <Outlet context={{ lang }} />
    </Layout>
  );
}

function WithLang({ component: Component }) {
  const { lang } = useParams();
  return <Component lang={lang} />;
}

/** Anchor redirect helper — preserves lang param, sends to consolidated page section. */
function AnchorRedirect({ to }) {
  const { lang } = useParams();
  return <Navigate to={`/${lang}${to}`} replace />;
}

/**
 * Query-preserving redirect: keeps the query string (e.g. ?release=v2.22.0)
 * intact when bouncing from a retired path to the new one.
 */
function PreservingRedirect({ to }) {
  const { lang } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/${lang}${to}${search}`} replace />;
}

/**
 * LangPrefixRedirect — when a visitor lands on a path without a language
 * segment (e.g. `/signboards`, `/whats-new?service=jec`), forward them
 * to the JA-prefixed equivalent so old links shared without the `/ja`
 * or `/en` prefix continue to resolve instead of 404-ing. If the path
 * after the redirect is genuinely unknown, the inner LangLayout's
 * catch-all still surfaces the friendly 404 page.
 */
function LangPrefixRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`/${DEFAULT_LANG}${pathname}${search}${hash}`} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <SamePagePulse />
      <Routes>
        <Route path="/" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />

        <Route path="/:lang" element={<LangLayout />}>
          <Route index element={<WithLang component={HomePage} />} />

          {/* Consolidated single pages */}
          <Route path="about" element={<WithLang component={AboutPage} />} />
          <Route path="services" element={<WithLang component={ServicesPage} />} />
          <Route path="services/:slug" element={<WithLang component={ServiceDetailPage} />} />
          <Route path="our-team" element={<WithLang component={OurTeamPage} />} />
          <Route path="ai-team" element={<AnchorRedirect to="/our-team" />} />
          <Route path="customers" element={<AnchorRedirect to="/about#pillars" />} />
          <Route path="approach" element={<AnchorRedirect to="/about" />} />
          <Route path="corporate-profile" element={<WithLang component={CorporateProfilePage} />} />
          <Route path="careers" element={<WithLang component={CareersPage} />} />
          <Route path="contact" element={<WithLang component={ContactPage} />} />
          <Route path="whats-new" element={<WithLang component={WhatsNewPage} />} />
          <Route path="roadmap" element={<WithLang component={RoadmapPage} />} />
          <Route path="testimonials" element={<WithLang component={TestimonialsPage} />} />
          <Route path="our-leaders" element={<WithLang component={LeadershipPage} />} />
          <Route path="signboards" element={<SignboardsPage />} />
          <Route path="signboards/map" element={<Navigate to={`/${DEFAULT_LANG}/signboards/map/mainland`} replace />} />
          <Route path="signboards/map/:scope" element={<MapDetailPage />} />
          <Route path="signboards/:slug" element={<SignboardDetailPage />} />
          <Route path="sitemap" element={<WithLang component={SitemapPage} />} />
          <Route path="founders-message" element={<FoundersMessagePage />} />
          <Route path="founders-message/:service" element={<FoundersMessagePage />} />
          <Route
            path="legal/commercial-transactions"
            element={<WithLang component={CommercialTransactionsPage} />}
          />
          <Route
            path="legal/privacy"
            element={<WithLang component={PrivacyPolicyPage} />}
          />
          <Route
            path="legal/terms"
            element={<WithLang component={TermsOfServicePage} />}
          />
          <Route
            path="terms"
            element={<PreservingRedirect to="/legal/terms" />}
          />
          <Route
            path="privacy"
            element={<PreservingRedirect to="/legal/privacy" />}
          />

          {/* Retired routes → redirect to new What's New / Roadmap pages */}
          <Route path="ecosystem" element={<PreservingRedirect to="/whats-new" />} />
          <Route path="changelog" element={<PreservingRedirect to="/whats-new" />} />
          <Route path="whats-next" element={<PreservingRedirect to="/roadmap" />} />

          {/* Consolidated About Us redirects */}
          <Route path="who-we-are" element={<AnchorRedirect to="/about" />} />
          <Route path="our-approach" element={<AnchorRedirect to="/about" />} />
          <Route path="trust-security" element={<WithLang component={TrustSecurityPage} />} />

          {/* Legacy route redirects → consolidated pages */}
          <Route path="company" element={<AnchorRedirect to="/corporate-profile" />} />
          <Route path="company/profile" element={<AnchorRedirect to="/corporate-profile" />} />
          <Route path="company/history" element={<AnchorRedirect to="/about#history" />} />
          <Route path="company/vision" element={<AnchorRedirect to="/about#vision" />} />
          <Route path="company/message" element={<AnchorRedirect to="/about#message" />} />
          <Route path="business" element={<AnchorRedirect to="/about#pillars" />} />
          <Route path="technology" element={<WithLang component={TechnologyPage} />} />

          {/* Unknown route inside a valid language scope → friendly 404 */}
          <Route path="*" element={<WithLang component={NotFoundPage} />} />
        </Route>

        {/* Top-level path without a language prefix (e.g. /signboards,
            /whats-new?service=jec). Forward to the JA-prefixed equivalent
            so external links shared without /ja or /en still resolve.
            Unknown paths fall through to the JA layout's friendly 404. */}
        {/* Admin: sync status — unlinked, language-agnostic, gated by
            `?key=<ADMIN_DIAGNOSTIC_TOKEN>` URL param on first visit
            (token then persists via localStorage so the header "Sync
            status" pill works on subsequent navigation). Both the old
            `/admin/federation-status` URL and the new `/admin/sync-
            status` URL route to the same page so existing bookmarks
            continue to work. */}
        <Route path="/admin/sync-status" element={<AdminSyncStatusPage />} />
        <Route path="/admin/federation-status" element={<AdminSyncStatusPage />} />
        <Route path="*" element={<LangPrefixRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}
